/**
 * Wire-mapping for the dual-auth governance approval queue (OIDCPAM Gate-6
 * Phase 6a — GovernanceDualAuth.tsx).
 *
 * `AdminGovernanceListActionsResp.actions` is typed `Array<Record<string,
 * unknown>>` in the generated client (gen/client/react/client.ts) — the Go
 * handler (AdminGovernanceListActions in admin_governance_server.go)
 * `jsonv2.MarshalWrite`s `[]*GovernanceAction` directly, bypassing the genapi
 * response-schema encoder, so the generator could not infer a concrete
 * response type. The wire JSON uses snake_case json tags (see
 * pam_dual_auth.go's GovernanceAction struct: id, tenant_id, kind,
 * proposer_id, status, proposed_at, expires_at, approver_id, approved_at,
 * ...), and the generated client's fromWire() converts EVERY response key
 * snake_case->camelCase before resolving — including nested
 * Record<string, unknown> array items, since the conversion is a blind
 * recursive key-walk, not schema-aware. So the REAL runtime shape at this
 * boundary is WireGovernanceAction below (camelCase), NOT the hand-written
 * snake_case GovernanceAction type in ../api/types.ts.
 *
 * NOT A LATENT BUG (unlike GovernanceRoles.tsx): the pre-migration page
 * worked around this correctly via a `deepSnake()` round-trip conversion
 * (camelCase -> snake_case) before storing the response, so
 * a.proposer_id/a.proposed_at/a.expires_at resolved correctly. This mapping
 * drops that redundant round-trip and maps camelCase -> camelCase
 * field-for-field onto @devnw/cyber-iam's iam/types#GovernanceAction shape,
 * pre-formatting proposedAt/expiresAt as display strings (the library never
 * parses dates itself).
 */
import type {
  GovernanceAction as IamGovernanceAction,
  GovernanceActionStatus,
} from '@devnw/cyber-iam/iam/types'

/** The real (camelCase) runtime shape of one AdminGovernanceListActionsResp action. */
export interface WireGovernanceAction {
  id: string
  kind: string
  proposerId: string
  status: GovernanceActionStatus
  proposedAt: string
  expiresAt: string
}

/** HOST-COMPUTED display formatting — the library never parses dates. */
function fmtDate(iso?: string): string | undefined {
  return iso ? new Date(iso).toLocaleString() : undefined
}

export function toIamGovernanceAction(
  w: WireGovernanceAction
): IamGovernanceAction {
  return {
    id: w.id,
    kind: w.kind,
    proposerId: w.proposerId,
    status: w.status,
    proposedAt: fmtDate(w.proposedAt),
    expiresAt: fmtDate(w.expiresAt),
  }
}
